"use client";

import { useRef, useState, useEffect, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible(threshold = 0.1): [RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible] as [RefObject<HTMLDivElement>, boolean];
}

function Reveal({ children, delay = 0, from = "bottom" }: {
  children: React.ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, v] = useVisible();
  const hidden = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return (
    <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">
      {text}
    </span>
  );
}
function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}
function CheckIcon() {
  return (
    <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/* ─── Data ────────────────────────────────────────────────── */
const SERVICES = [
  {
    n: "01",
    title: "Paid Ads (PPC & Social Advertising)",
    body: "Data-driven paid advertising across Google Ads, Meta (Facebook & Instagram), LinkedIn and YouTube. Audience research, ad creatives, bidding strategy, A/B testing and continuous optimisation to maximise CTR and minimise cost per acquisition.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["Google Ads", "Meta / Instagram", "LinkedIn Ads", "YouTube"],
  },
  {
    n: "02",
    title: "SEO (Search Engine Optimization)",
    body: "Rank higher on Google and drive sustainable organic traffic. Technical SEO audits, on-page optimisation, keyword research, content strategy and high-authority link building — found by the right people, without paying per click.",
    img: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&q=80",
    tags: ["Technical SEO", "Link building", "Content strategy", "Keyword research"],
  },
  {
    n: "03",
    title: "Lead Generation",
    body: "Targeted campaigns that fill your sales pipeline with qualified prospects. Landing pages, lead magnets, gated content and multi-channel outreach — across paid, organic and email — attracting decision-makers actively looking for what you offer.",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80",
    tags: ["Landing pages", "Lead magnets", "Multi-channel", "Pipeline filling"],
  },
  {
    n: "04",
    title: "Sales Funnels",
    body: "From first click to final sale — we architect high-converting funnels tailored to your buyer's journey. Awareness ads, lead capture, nurture sequences, offer pages, upsells and follow-up automations — every stage moves prospects closer to a yes.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    tags: ["Funnel architecture", "Nurture sequences", "Upsells", "Automation"],
  },
  {
    n: "05",
    title: "Marketing Automation",
    body: "Stop losing leads to slow follow-ups. Intelligent automation workflows using HubSpot, Mailchimp, ActiveCampaign and Zapier — email sequences, lead scoring, CRM sync, drip campaigns and behavioural triggers — nurturing leads 24/7.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["HubSpot / Mailchimp", "Lead scoring", "Drip campaigns", "CRM sync"],
  },
  {
    n: "06",
    title: "CRO (Conversion Rate Optimization)",
    body: "Getting traffic is only half the battle. Heatmaps, session recordings and funnel analytics identify exactly where visitors drop off — then we fix it. Landing page redesigns, copy testing, form optimisation and CTA experiments.",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=600&q=80",
    tags: ["Heatmaps", "A/B testing", "Landing page CRO", "Funnel analytics"],
  },
  {
    n: "07",
    title: "Analytics & Reporting",
    body: "End-to-end marketing analytics — GA4, Google Tag Manager, Meta Pixel, conversion tracking and custom dashboards — giving you a real-time view of which channels, campaigns and creatives are driving actual revenue, not vanity metrics.",
    img: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80",
    tags: ["Google Analytics 4", "GTM setup", "Custom dashboards", "Attribution"],
  },
];

const TOOLS = [
  { channel: "Paid Ads",                  tools: "Google Ads, Meta Ads Manager, LinkedIn Campaign Manager, YouTube Ads, Microsoft Advertising" },
  { channel: "SEO",                        tools: "Ahrefs, SEMrush, Google Search Console, Screaming Frog, Surfer SEO, Moz" },
  { channel: "Automation & CRM",          tools: "HubSpot, ActiveCampaign, Mailchimp, Zoho CRM, Zapier, Make (Integromat)" },
  { channel: "Funnels & Landing Pages",   tools: "ClickFunnels, Unbounce, Instapage, WordPress + Elementor, Webflow" },
  { channel: "Analytics & CRO",           tools: "Google Analytics 4, Google Tag Manager, Hotjar, Microsoft Clarity, Optimizely, VWO" },
];

const BENEFITS = [
  { title: "Guaranteed Measurable ROI",     body: "Every campaign tied to real outcomes — leads, sales, revenue — not just impressions or clicks." },
  { title: "Lower Customer Acquisition Cost",body: "Continuous optimisation reduces cost-per-lead and cost-per-sale as campaigns mature and data accumulates." },
  { title: "Faster Revenue Growth",         body: "Bottom-funnel strategies target buyers ready to act, cutting your sales cycle significantly." },
  { title: "Scalable Lead Pipeline",         body: "A proven funnel scales on demand — more budget means more leads and more revenue, predictably." },
  { title: "Full-Funnel Visibility",         body: "Know exactly which channel, ad, or keyword generated each lead and sale — zero guesswork, complete attribution." },
  { title: "24/7 Automated Nurturing",       body: "Marketing automation keeps your brand top-of-mind and nurtures leads even while your sales team is offline." },
];

const STEPS = [
  { n: "01", title: "Audit & Strategy",                  body: "Deep-dive audit of your current marketing, competitors and goals. We define KPIs, funnel structure, channel mix and a 90-day growth roadmap before spending a single rupee." },
  { n: "02", title: "Campaign Setup & Tracking",         body: "Pixel installation, conversion tracking, GTM configuration, CRM integration and analytics setup. 100% data accuracy from day one — every decision backed by clean numbers." },
  { n: "03", title: "Ad Creative & Landing Pages",       body: "Compelling ad copy, scroll-stopping creatives and high-converting landing pages built for your audience and offer — designed to capture attention and drive action." },
  { n: "04", title: "Campaign Launch & A/B Testing",     body: "Multi-variant launches across selected channels with systematic A/B testing of audiences, creatives, headlines and offers. Data — not opinions — decides what works." },
  { n: "05", title: "Optimisation & Scaling",            body: "Weekly performance reviews, bid adjustments, audience refinement, budget reallocation to top performers. We cut losers fast and scale winners aggressively." },
  { n: "06", title: "Reporting & Growth Planning",       body: "Transparent monthly reports with clear ROI metrics, campaign insights and next-phase recommendations. You always know what's working, what's not and what we're doing." },
];

const WHO_FOR = [
  { segment: "Startups & D2C Brands",       desc: "Acquire customers fast, test product-market fit and scale revenue without a massive traditional marketing budget." },
  { segment: "B2B Companies & SaaS",        desc: "Drive a consistent flow of qualified leads, shorter sales cycles and measurable pipeline value from marketing spend." },
  { segment: "E-commerce Businesses",       desc: "Reduce cart abandonment, improve ROAS on paid campaigns and grow repeat purchase rates through automation." },
  { segment: "Local & Service Businesses",  desc: "Dominate local search, generate inbound calls and form fills and outrank competitors on Google Maps and organic results." },
  { segment: "Enterprises & Large Brands",  desc: "Transparent multi-channel attribution, optimised media buying and data-driven growth strategies at scale." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
const FAQS = [
  { q: "What is performance marketing and how is it different from traditional marketing?",   a: "Performance marketing is a results-first approach where you only pay for measurable outcomes — clicks, leads, or sales. Unlike traditional marketing where spend is tied to reach or impressions, every rupee in performance marketing is tracked to a specific business result. We set up full attribution so you know exactly which channel generated each lead and sale." },
  { q: "How long before I see results from performance marketing?",                          a: "Paid ads (PPC) typically generate leads within 7–14 days of launch. SEO takes 3–6 months to build meaningful organic traffic. Sales funnels and marketing automation show measurable lift within 30–60 days. We set clear, channel-specific timelines during the strategy phase." },
  { q: "What budget do I need to start performance marketing?",                             a: "Paid ad campaigns typically require a minimum monthly ad spend of ₹30,000–₹50,000 to generate meaningful data and results. SEO and marketing automation have lower ongoing costs. We recommend a total initial budget based on your goals and chosen channels during the audit phase." },
  { q: "Do you manage ads on all platforms or just Google?",                                a: "We manage campaigns across Google Ads, Meta (Facebook & Instagram), LinkedIn, YouTube and Microsoft Advertising — depending on where your target audience is most active. We recommend the right channel mix based on your business type, product and customer profile." },
  { q: "How do you measure and report on campaign performance?",                            a: "We set up end-to-end tracking using Google Analytics 4, Meta Pixel, Google Tag Manager and CRM integration. You receive transparent monthly reports with real ROI metrics — cost per lead, conversion rate, ROAS, pipeline value — not vanity metrics like impressions or reach." },
  { q: "Can you work with our existing CRM and marketing tools?",                           a: "Yes. We integrate with HubSpot, Zoho CRM, Salesforce, Mailchimp, ActiveCampaign and most major marketing platforms. We audit your existing stack and recommend integrations that eliminate gaps in your lead nurturing and attribution workflow." },
];

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && <div className="pb-[22px]"><p className="text-[14px] text-slate-500 leading-relaxed">{a}</p></div>}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard({ s, i }: { s: typeof SERVICES[0]; i: number }) {
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 4) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[165px] overflow-hidden">
        <img src={s.img} alt={s.title} className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}/>
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">{s.n}</div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[13.5px] font-bold text-slate-900 mb-2 leading-snug">{s.title}</p>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-3.5">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step({ s, i, last }: { s: typeof STEPS[0]; i: number; last: boolean }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start relative transition-all duration-500 ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${i * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── ToolRow ─────────────────────────────────────────────── */
function ToolRow({ row, i }: { row: { channel: string; tools: string }; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`grid grid-cols-[1fr_1.8fr] gap-4 px-4 py-4 border-b border-slate-200 items-start transition-all duration-500 ${v ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"} ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
      style={{ transitionDelay: `${i * 0.08}s` }}
    >
      <span className="text-[13px] font-bold text-slate-900 leading-snug">{row.channel}</span>
      <span className="text-[13px] text-slate-500 leading-relaxed">{row.tools}</span>
    </div>
  );
}

/* ─── WhoForCard ──────────────────────────────────────────── */
function WhoForCard({ w, i }: { w: { segment: string; desc: string }; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-6 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${i * 0.09}s` }}
    >
      <div className="w-8 h-8 bg-blue-700 flex items-center justify-center mb-4">
        <span className="text-[11px] font-black text-white">{String(i + 1).padStart(2, "0")}</span>
      </div>
      <p className="text-[13.5px] font-bold text-slate-900 mb-2.5 leading-snug">{w.segment}</p>
      <p className="text-[13px] text-slate-500 leading-relaxed">{w.desc}</p>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function PerformanceMarketing() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[600px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[90px] bg-white">

          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/marketing" className="text-blue-700 font-medium hover:underline">Marketing</Link>
            <span className="text-slate-300">›</span>
            <span>Performance Marketing</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["Google Ads", "Meta Ads", "SEO", "Lead Generation", "CRO", "Marketing Automation"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="Digital Marketing — Performance Marketing" />
          <Divider />

          <h1 className="text-[clamp(24px,3.2vw,44px)] font-extrabold text-slate-900 leading-[1.1] mb-5 max-w-[580px]">
            Turn Traffic Into Revenue — Performance Marketing That Delivers Measurable ROI
          </h1>
          <p className="text-[15.5px] font-light text-slate-500 leading-relaxed mb-9 ">
            From paid ads and SEO to lead generation, sales funnels, CRO and marketing automation — we build and manage full-funnel performance marketing systems. Every rupee you spend is tracked, optimised and justified.
          </p>
          <div className="flex gap-3.5 flex-wrap">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              GET FREE AUDIT
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              VIEW OUR WORK
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=900&q=85"
            alt="Performance marketing analytics"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "7",      l: "Marketing Services" },
          { v: "Full",   l: "Funnel Coverage" },
          { v: "100%",   l: "Tracked & Attributed" },
          { v: "Free",   l: "Marketing Audit" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ SERVICES — 4-col grid (7 cards) ═══════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="Performance Marketing Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Seven Capabilities.<br />One Full-Funnel System.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[380px] leading-relaxed">
                  Every service is designed to connect to the next — from attracting traffic to converting it into revenue.
                </p>
              </div>
            </div>
          </Reveal>

          {/* 4 + 3 layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            {SERVICES.slice(0, 4).map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.slice(4).map((s, i) => <ServiceCard key={i} s={s} i={i + 4} />)}
          </div>
        </div>
      </section>

      {/* ══ BENEFITS + IMAGE ═══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="Why Performance Marketing" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Marketing That Pays for Itself
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Unlike traditional marketing that spends budget on reach and hope, performance marketing is built on accountability — every campaign is measured against real business outcomes.
            </p>
            <div className="flex flex-col gap-0">
              {BENEFITS.map((b, i) => (
                <div key={i} className={`flex items-start gap-4 py-5 ${i < BENEFITS.length - 1 ? "border-b border-slate-100" : ""}`}>
                  <CheckIcon />
                  <div>
                    <p className="text-[14px] font-bold text-slate-900 mb-1">{b.title}</p>
                    <p className="text-[13.5px] text-slate-500 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80"
                alt="Performance marketing ROI"
                className="w-full h-[500px] object-cover"
              />
              <div className="hidden lg:block absolute bottom-[-24px] right-[-24px] bg-white border border-slate-200 shadow-xl px-6 py-5">
                <p className="text-[30px] font-black text-blue-700 leading-none">100%</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Spend Attributed</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">24/7</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Automated Nurturing</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ TOOLS & PLATFORMS — table + image ══════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Tools & Platforms" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Best-in-Class Marketing Technology Stack
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              We work with the industry&apos;s leading platforms — so your campaigns are built on proven, reliable infrastructure with access to the most powerful targeting and optimisation capabilities available.
            </p>

            <div>
              <div className="grid grid-cols-[1fr_1.8fr] bg-blue-700 px-4 py-3">
                <span className="text-[11px] font-bold text-white tracking-[0.1em] uppercase">Channel / Service</span>
                <span className="text-[11px] font-bold text-white tracking-[0.1em] uppercase">Tools & Platforms</span>
              </div>
              {TOOLS.map((row, i) => (
                <ToolRow key={i} row={row} i={i} />
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <img
              src="https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=700&q=80"
              alt="Marketing tools"
              className="w-full h-[540px] object-cover sticky top-24"
            />
          </Reveal>
        </div>
      </section>

      {/* ══ 6-STAGE PROCESS ════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Our Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              6-Stage Performance Marketing Process
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A structured, data-first process — from strategy to scaling. No guesswork, no vanity metrics. Just a clear system that generates and converts leads at scale.
            </p>
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=700&q=80"
              alt="Marketing strategy process"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              {STEPS.map((s, i) => <Step key={i} s={s} i={i} last={i === STEPS.length - 1} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ WHO IS IT FOR — 5 segments ═════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-8 lg:mb-12">
              <Eyebrow text="Who Is Performance Marketing For" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Built for Every Growth Stage
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[380px] leading-relaxed">
                  Whether you&apos;re a seed-stage startup or an enterprise brand, performance marketing scales with your ambition.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0.5">
            {WHO_FOR.map((w, i) => (
              <WhoForCard key={i} w={w} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Performance Marketing
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear, honest answers to the performance marketing questions we hear most from Indian businesses.
            </p>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80"
              alt="Performance marketing FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Grow?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Get a Free Performance Marketing Audit
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              We audit your current marketing setup — ads, SEO, funnels and analytics — identify gaps and quick wins and present a 90-day growth roadmap.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              No commitment, no generic advice. A real audit built around your business, your goals and your current numbers.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                GET FREE AUDIT
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                TALK TO A SPECIALIST
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}